import { useState } from "react";
import { createPortal } from "react-dom";
import { X, Copy, Download, Check, Loader2, FileSignature, CalendarClock } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";

export default function LetterModal({ open, onClose, loading, contractName, letter, onReminderSet }) {
  const [copied, setCopied] = useState(false);
  const today = new Date().toISOString().slice(0, 10);
  const [showReminder, setShowReminder] = useState(false);
  const [reminderDate, setReminderDate] = useState(today);
  const [savingReminder, setSavingReminder] = useState(false);

  if (!open) return null;

  const copy = async () => {
    await navigator.clipboard.writeText(letter || "");
    setCopied(true);
    toast.success("Lettre copiée");
    setTimeout(() => setCopied(false), 1800);
  };

  const download = () => {
    const blob = new Blob([letter || ""], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `resiliation-${(contractName || "contrat").replace(/\s+/g, "-").toLowerCase()}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const setReminder = async () => {
    if (!reminderDate) return;
    setSavingReminder(true);
    try {
      await api.post("/contracts/reminder", { contract_name: contractName, renewal_date: reminderDate });
      toast.success("Rappel programmé — retrouvez-le dans l'onglet Échéances");
      onReminderSet?.();
      setShowReminder(false);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Impossible de programmer le rappel");
    } finally {
      setSavingReminder(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-foreground/40 backdrop-blur-sm" onClick={onClose} data-testid="letter-modal">
      <div className="w-full max-w-2xl max-h-[85vh] flex flex-col rounded-2xl border border-border bg-card shadow-xl animate-in-up" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-border">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-primary/10 text-primary grid place-items-center">
              <FileSignature className="w-4 h-4" />
            </div>
            <div>
              <p className="font-heading font-bold leading-none">Lettre de résiliation</p>
              <p className="text-xs text-muted-foreground mt-1">{contractName}</p>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 grid place-items-center rounded-lg hover:bg-muted transition" data-testid="letter-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto scrollbar-thin p-5">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-16 gap-3 text-muted-foreground">
              <Loader2 className="w-6 h-6 animate-spin text-primary" />
              <p className="text-sm">Rédaction de votre lettre recommandée…</p>
            </div>
          ) : (
            <pre className="whitespace-pre-wrap font-sans text-sm leading-relaxed text-foreground">{letter}</pre>
          )}
        </div>

        {!loading && (
          <div className="border-t border-border">
            {showReminder && (
              <div className="p-4 border-b border-border bg-muted/30 flex flex-wrap items-center gap-3" data-testid="reminder-panel">
                <label className="text-sm text-muted-foreground">Date de rappel :</label>
                <input
                  data-testid="reminder-date-input"
                  type="date"
                  value={reminderDate}
                  min={today}
                  onChange={(e) => setReminderDate(e.target.value)}
                  className="h-10 px-3 rounded-lg border border-input bg-card text-sm outline-none focus:ring-2 focus:ring-ring/40"
                />
                <button
                  data-testid="reminder-confirm-btn"
                  onClick={setReminder}
                  disabled={savingReminder || !reminderDate}
                  className="h-10 px-4 rounded-xl bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 disabled:opacity-60 transition inline-flex items-center gap-2"
                >
                  {savingReminder ? <Loader2 className="w-4 h-4 animate-spin" /> : <CalendarClock className="w-4 h-4" />} Programmer
                </button>
              </div>
            )}
            <div className="p-4 flex flex-wrap items-center justify-between gap-2">
              <button
                data-testid="reminder-toggle-btn"
                onClick={() => setShowReminder((s) => !s)}
                className="inline-flex items-center gap-2 h-10 px-4 rounded-xl border border-border text-sm font-medium hover:bg-muted transition"
              >
                <CalendarClock className="w-4 h-4" /> Me rappeler de résilier
              </button>
              <div className="flex items-center gap-2">
                <button onClick={copy} data-testid="letter-copy" className="flex items-center gap-2 h-10 px-4 rounded-xl border border-border text-sm font-medium hover:bg-muted transition">
                  {copied ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />} Copier
                </button>
                <button onClick={download} data-testid="letter-download" className="flex items-center gap-2 h-10 px-4 rounded-xl bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 transition">
                  <Download className="w-4 h-4" /> Télécharger
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}
